import { Link, useParams } from "react-router-dom";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, ArrowLeft, ShieldCheck, Star, Loader2, Lock, Landmark } from "lucide-react";
import { isActiveSponsor } from "@/lib/funderSort";
import { useFeatureGate } from "@/hooks/useSubscription";

const FunderDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const canDeepView = useFeatureGate("funder_deep_view");

  const { data: funder, isLoading } = useQuery({
    queryKey: ["funder", slug],
    enabled: !!slug,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("funders")
        .select("*")
        .eq("slug", slug!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const { data: programs } = useQuery({
    queryKey: ["funder-programs", funder?.id],
    enabled: !!funder?.id,
    queryFn: async () => {
      const { data } = await supabase
        .from("grant_programs")
        .select("id, title, program_url, source_url, focus_areas, award_min, award_max, cycle, next_deadline, eligibility, description")
        .eq("funder_id", funder!.id)
        .eq("verified", true)
        .order("title");
      return data ?? [];
    },
  });

  const { data: profile990 } = useQuery({
    queryKey: ["funder-990-profile", funder?.id],
    enabled: !!funder?.id && canDeepView,
    queryFn: async () => {
      const { data } = await supabase
        .from("funder_990_profiles")
        .select("ein, total_giving, avg_grant, grants_count, fiscal_year, foundation_grants_paid, is_private_foundation, ntee_category, openness_score")
        .eq("funder_id", funder!.id)
        .maybeSingle();
      return data;
    },
  });

  const { data: grantsPaid } = useQuery({
    queryKey: ["funder-990-grants", profile990?.ein],
    enabled: !!profile990?.ein && canDeepView,
    queryFn: async () => {
      const { data } = await supabase
        .from("funder_990_grants")
        .select("recipient_name, recipient_city, recipient_state, amount, purpose, fiscal_year")
        .eq("ein", profile990!.ein)
        .order("amount", { ascending: false })
        .limit(50);
      return data ?? [];
    },
  });

  useEffect(() => {
    if (funder?.name) document.title = `${funder.name} | Funder | emrkt.co`;
  }, [funder?.name]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="container mx-auto px-6 py-32 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!funder) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="container mx-auto px-6 pt-32 pb-16 max-w-2xl">
          <h1 className="font-display text-3xl font-bold mb-3">Funder not found</h1>
          <p className="text-muted-foreground mb-6">We couldn't find that funder.</p>
          <Link to="/opportunities">
            <Button variant="outline">Back to opportunities</Button>
          </Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="container mx-auto px-6 pt-24 pb-16 max-w-4xl">
        <Link to="/opportunities" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="h-4 w-4 mr-1" /> Opportunities
        </Link>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Badge variant="outline">{funder.funder_type}</Badge>
          {funder.verified && (
            <Badge variant="outline" className="gap-1 bg-emerald-500/10 text-emerald-600 border-emerald-500/30">
              <ShieldCheck className="h-3 w-3" /> Verified
            </Badge>
          )}
          {isActiveSponsor(funder as any) && (
            <Badge variant="outline" className="gap-1 bg-amber-500/10 text-amber-600 border-amber-500/30">
              <Star className="h-3 w-3" /> {funder.sponsor_tier === "premium" ? "Premium partner" : "Featured partner"}
            </Badge>
          )}
        </div>

        <h1 className="font-display text-4xl font-bold mb-3">{funder.name}</h1>
        {funder.description && (
          <p className="text-base text-muted-foreground mb-6 max-w-3xl">{funder.description}</p>
        )}

        <div className="grid sm:grid-cols-3 gap-3 mb-10 text-sm">
          {funder.typical_award_range && (
            <div className="bg-card border border-border rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Typical award</div>
              <div className="font-medium">{funder.typical_award_range}</div>
            </div>
          )}
          {funder.hq_state && (
            <div className="bg-card border border-border rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Headquartered</div>
              <div className="font-medium">{funder.hq_state}</div>
            </div>
          )}
          {funder.geographies?.length > 0 && (
            <div className="bg-card border border-border rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Geographies</div>
              <div className="font-medium">{funder.geographies.join(", ")}</div>
            </div>
          )}
        </div>

        {funder.focus_areas?.length > 0 && (
          <div className="mb-8">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Focus areas</div>
            <div className="flex flex-wrap gap-2">
              {funder.focus_areas.map((f: string) => (
                <Badge key={f} variant="secondary">{f}</Badge>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-12">
          {funder.website && (
            <a href={funder.website} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" className="gap-2">
                Visit website <ExternalLink className="h-3.5 w-3.5" />
              </Button>
            </a>
          )}
          {funder.source_url && funder.source_url !== funder.website && (
            <a href={funder.source_url} target="_blank" rel="noopener noreferrer">
              <Button variant="ghost" size="sm" className="gap-2">
                Source <ExternalLink className="h-3.5 w-3.5" />
              </Button>
            </a>
          )}
        </div>

        <h2 className="font-display text-2xl font-semibold mb-4">Active programs</h2>
        {programs && programs.length > 0 ? (
          <div className="grid sm:grid-cols-2 gap-4">
            {programs.map((p: any) => (
              <div key={p.id} className="border border-border rounded-lg p-4 bg-card">
                <h3 className="font-semibold text-sm mb-2">{p.title}</h3>
                {p.description && (
                  <p className="text-xs text-muted-foreground mb-3">{p.description}</p>
                )}
                <div className="flex flex-wrap gap-2 text-[10px] mb-3">
                  {p.cycle && <Badge variant="outline">{p.cycle}</Badge>}
                  {(p.award_min || p.award_max) && (
                    <Badge variant="outline">
                      ${(p.award_min ?? 0).toLocaleString()} – ${(p.award_max ?? p.award_min ?? 0).toLocaleString()}
                    </Badge>
                  )}
                </div>
                {p.program_url && (
                  <a href={p.program_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                    View program <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No verified programs listed yet.</p>
        )}

        <div className="mt-16 pt-10 border-t border-border">
          <div className="flex items-center gap-2 mb-2">
            <Landmark className="h-4 w-4 text-primary" />
            <h2 className="font-display text-2xl font-semibold">990 financials & grants paid</h2>
            <Badge variant="outline" className="text-[10px] uppercase tracking-wide">Pro</Badge>
          </div>
          <p className="text-xs text-muted-foreground mb-5">
            Source: IRS Form 990 via ProPublica Nonprofit Explorer.
          </p>

          {!canDeepView ? (
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-6">
              <div className="flex items-start gap-3">
                <Lock className="h-5 w-5 text-primary mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-sm mb-1">Deep funder view is a Pro feature</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Unlock 990 financials (total giving, average grant, openness score) and the most-recent grants paid by this funder.
                  </p>
                  <Button asChild size="sm">
                    <Link to="/pricing">See plans</Link>
                  </Button>
                </div>
              </div>
            </div>
          ) : !profile990 ? (
            <p className="text-sm text-muted-foreground">No 990 profile linked yet for this funder.</p>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                {profile990.total_giving != null && (
                  <div className="bg-card border border-border rounded-lg p-3">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Total giving</div>
                    <div className="font-semibold">${Number(profile990.total_giving).toLocaleString()}</div>
                  </div>
                )}
                {profile990.foundation_grants_paid != null && (
                  <div className="bg-card border border-border rounded-lg p-3">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Grants paid (990-PF)</div>
                    <div className="font-semibold">${Number(profile990.foundation_grants_paid).toLocaleString()}</div>
                  </div>
                )}
                {profile990.avg_grant != null && (
                  <div className="bg-card border border-border rounded-lg p-3">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Avg grant</div>
                    <div className="font-semibold">${Number(profile990.avg_grant).toLocaleString()}</div>
                  </div>
                )}
                {profile990.fiscal_year && (
                  <div className="bg-card border border-border rounded-lg p-3">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Fiscal year</div>
                    <div className="font-semibold">{profile990.fiscal_year}</div>
                  </div>
                )}
              </div>

              <h3 className="font-display text-lg font-semibold mb-3">Recent grants paid</h3>
              {grantsPaid && grantsPaid.length > 0 ? (
                <div className="overflow-x-auto border border-border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="text-left px-3 py-2">Recipient</th>
                        <th className="text-left px-3 py-2">Location</th>
                        <th className="text-right px-3 py-2">Amount</th>
                        <th className="text-left px-3 py-2">Purpose</th>
                        <th className="text-right px-3 py-2">FY</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grantsPaid.map((g: any, i: number) => (
                        <tr key={i} className="border-t border-border">
                          <td className="px-3 py-2 font-medium">{g.recipient_name ?? "—"}</td>
                          <td className="px-3 py-2 text-muted-foreground">
                            {[g.recipient_city, g.recipient_state].filter(Boolean).join(", ") || "—"}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {g.amount != null ? `$${Number(g.amount).toLocaleString()}` : "—"}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">{g.purpose ?? "—"}</td>
                          <td className="px-3 py-2 text-right text-muted-foreground">{g.fiscal_year ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No itemized grants found in the latest 990 filing.</p>
              )}
            </>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default FunderDetail;